/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useCallback } from 'react';
import type { FormikErrors, FormikHelpers, FormikValues } from 'formik';
import * as Yup from 'yup';
import classNames from 'classnames';
import type { Segment } from '@fixter/i18n';
import { isValidPhoneNumber } from '@fixter/i18n';

import type { IFormProps } from '$components/composite/Form';
import Form from '$components/composite/Form';
import FormItem from '$components/composite/Form/FormItem';
import Input from '$components/composite/Form/Input';
import { Link } from '$components/basic/Link';
import { PasswordInput } from '$components/composite/Form/PaswordInput';
import { Card } from '$src-components/atoms/Card';
import Banner from '$components/basic/Banner';
import { BannerThemeTypes } from '$components/basic/Banner/themedBanner';

import { getShortTextByKey, renderRichTextByKey } from '$cms/getDataByKey';
import type { CmsContentDictionary } from '$cms/index';
import { useAppContext } from '$util/AppContext';
import { Button, DesignType } from '@/components/atoms/Button';

import { Header } from '../PersonalDetailsHeader';
import { getSegmentFromLocale } from '../../../../../../util/getSegment';
import { Styled } from './styled';

const getValidationSchema = (
  hasPurchaseOrder: boolean,
  isGuestUser: boolean,
  errorMessages: any,
  segment: Segment
) => {
  const {
    firstNameErrorMin,
    firstNameErrorMax,
    firstNameErrorRequired,
    lastNameErrorMin,
    lastNameErrorMax,
    lastNameErrorRequired,
    mobileNumberErrorMatches,
    mobileNumberErrorRequired,
    emailErrorInvalid,
    emailErrorMax,
    emailErrorRequired,
    passwordErrorRequired,
    purchaseOrderErrorMax,
    purchaseOrderErrorRequired,
  } = errorMessages;

  const shape = {
    firstName: Yup.string()
      .trim()
      // eslint-disable-next-line no-template-curly-in-string
      .min(1, firstNameErrorMin)
      // eslint-disable-next-line no-template-curly-in-string
      .max(35, firstNameErrorMax)
      .required(firstNameErrorRequired),
    lastName: Yup.string()
      .trim()
      // eslint-disable-next-line no-template-curly-in-string
      .min(1, lastNameErrorMin)
      // eslint-disable-next-line no-template-curly-in-string
      .max(35, lastNameErrorMax)
      .required(lastNameErrorRequired),
    mobileNumber: Yup.string()
      .trim()
      .test('mobile-number-regex', mobileNumberErrorMatches, (value) => {
        const mobileNumber = value?.replace(/ /g, '') || '';
        return isValidPhoneNumber(mobileNumber, segment);
      })
      .required(mobileNumberErrorRequired),
    email: isGuestUser
      ? Yup.string()
          .trim()
          .email(emailErrorInvalid)
          // eslint-disable-next-line no-template-curly-in-string
          .max(320, emailErrorMax)
          .required(emailErrorRequired)
      : Yup.string(),
    password: isGuestUser ? Yup.string().required(passwordErrorRequired) : Yup.string(),
    purchaseOrder: hasPurchaseOrder
      ? Yup.string()
          .trim()
          // eslint-disable-next-line no-template-curly-in-string
          .max(255, purchaseOrderErrorMax)
          .required(purchaseOrderErrorRequired)
      : Yup.string(),
  };

  return Yup.object().shape(shape);
};
export interface PersonalDetails {
  readonly firstName: string;
  readonly lastName: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly password: string;
  readonly marketingConsent: boolean;
  readonly purchaseOrder: string;
}

export interface PersonalDetailsProps {
  readonly values: Readonly<Partial<PersonalDetails>>;
  readonly isGuestUser: boolean;
  readonly hasPurchaseOrder: boolean;
  readonly formContext?: IFormProps['formContext'];
  readonly onSubmit: (values: FormikValues, helpers?: FormikHelpers<FormikValues>) => void;
  readonly showFormHeading?: boolean;
  readonly inFormCta?: string;
  readonly loading?: boolean;
  readonly error?: JSX.Element | string;
  readonly className?: string;
}

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getRichText = renderRichTextByKey.bind(cmsData);

  return {
    passwordErrorMessage: getShortText(
      'checkout.personalDetails.contactForm.passwordErrorMessage',
      'Please enter a strong password'
    ),
    firstName: getShortText('checkout.personalDetails.contactForm.firstName', 'First Name'),
    lastName: getShortText('checkout.personalDetails.contactForm.lastName', 'Last Name'),
    phoneNumber: getShortText('checkout.personalDetails.contactForm.phoneNumber', 'Phone Number'),
    phoneNumberDescription: getShortText(
      'checkout.personalDetails.contactForm.phoneNumberDescription',
      "So we can contact you on the day of your booking when we're on our way."
    ),
    emailAddress: getShortText('checkout.personalDetails.contactForm.emailAddress', 'Email address'),
    noSpam: getShortText('checkout.personalDetails.contactForm.noSpam', 'No spam, promise.'),
    purchaseOrder: getShortText('checkout.personalDetails.contactForm.purchaseOrder', 'Purchase Order'),
    createPassword: getShortText('checkout.personalDetails.contactForm.createPassword', 'Create password'),
    exclusiveDiscountsMessage: getShortText(
      'checkout.personalDetails.contactForm.exclusiveDiscountsMessage',
      'Get exclusive discounts and money-saving tips straight to your inbox. No spam, we promise.'
    ),

    termsAndConditions: getRichText(
      'checkout.personalDetails.contactForm.termsAndConditions',
      <>
        By continuing you accept the{' '}
        <Link href="/terms-conditions" target="__blank">
          Terms and Conditions
        </Link>{' '}
        and{' '}
        <Link href="/privacy-policy" target="__blank">
          Privacy Policy
        </Link>
      </>
    ),
    firstNameErrorMin: getShortText(
      'checkout.personalDetails.contactForm.firstNameErrorMin',
      'Your first name must be 1 or more characters'
    ),
    firstNameErrorMax: getShortText(
      'checkout.personalDetails.contactForm.firstNameErrorMax',
      'First name must be below 35 characters'
    ),
    firstNameErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.firstNameErrorRequired',
      'Please enter your first name'
    ),
    lastNameErrorMin: getShortText(
      'checkout.personalDetails.contactForm.lastNameErrorMin',
      'Your last name must be 1 or more characters'
    ),
    lastNameErrorMax: getShortText(
      'checkout.personalDetails.contactForm.lastNameErrorMax',
      'Last name must be below 35 characters'
    ),
    lastNameErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.lastNameErrorRequired',
      'Please enter your last name'
    ),
    mobileNumberErrorMin: getShortText(
      'checkout.personalDetails.contactForm.mobileNumberErrorMin',
      'That mobile number looks wrong'
    ),
    mobileNumberErrorMatches: getShortText(
      'checkout.personalDetails.contactForm.mobileNumberErrorMatches',
      'That mobile number looks wrong'
    ),
    mobileNumberErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.mobileNumberErrorRequired',
      'Please enter your phone number'
    ),
    emailErrorInvalid: getShortText(
      'checkout.personalDetails.contactForm.emailErrorInvalid',
      "That email address doesn't look right"
    ),
    emailErrorMax: getShortText(
      'checkout.personalDetails.contactForm.emailErrorMax',
      'Email must be below 320 characters'
    ),
    emailErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.emailErrorRequired',
      'Please enter your email address'
    ),
    passwordErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.passwordErrorRequired',
      'Please enter your password'
    ),
    purchaseOrderErrorMax: getShortText(
      'checkout.personalDetails.contactForm.purchaseOrderErrorMax',
      'Purchase order must be below 255 characters'
    ),
    purchaseOrderErrorRequired: getShortText(
      'checkout.personalDetails.contactForm.purchaseOrderErrorRequired',
      'Please enter a purchase order for reference use'
    ),
  };
};

function PersonalDetailsForm({
  values,
  error,
  onSubmit,
  isGuestUser,
  hasPurchaseOrder,
  formContext,
  showFormHeading = true,
  inFormCta,
  loading,
  className,
  ...props
}: PersonalDetailsProps): JSX.Element {
  const AppContext = useAppContext();
  const {
    cmsContent: { dictionary },
    locale,
  } = AppContext;

  const segment = getSegmentFromLocale(locale);

  const {
    passwordErrorMessage,
    firstName,
    lastName,
    phoneNumber,
    phoneNumberDescription,
    emailAddress,
    noSpam,
    purchaseOrder,
    createPassword,
    exclusiveDiscountsMessage,
    termsAndConditions,
    ...errorMessages
  } = getCmsData(dictionary);

  const [validPassword, setValidPassword] = useState<boolean>(false);

  const validateForm = useCallback((): FormikErrors<FormikValues> => {
    const errors: FormikErrors<FormikValues> = {};
    if (isGuestUser && !validPassword) errors.password = passwordErrorMessage;
    return errors;
  }, [isGuestUser, passwordErrorMessage, validPassword]);

  const checkPasswordValidity = (valid: boolean) => setValidPassword(valid);

  return (
    <Styled className={classNames('PersonalDetails', className)} {...props}>
      {error && <Banner type={BannerThemeTypes.Danger}>{error}</Banner>}
      <Card className="card" cornerRadius={1}>
        {showFormHeading && <Header isGuestUser={isGuestUser} />}
        <Form
          formik={{
            initialValues: values,
            validationSchema: useCallback(
              () => getValidationSchema(hasPurchaseOrder, isGuestUser, errorMessages, segment),
              [errorMessages, hasPurchaseOrder, isGuestUser, segment]
            ),
            onSubmit,
            validateOnChange: false,
            validateOnBlur: false,
            validate: validateForm,
          }}
          formContext={formContext}
          noValidate
        >
          <div className="input-group">
            <FormItem name="firstName" label={firstName}>
              <Input />
            </FormItem>
            <FormItem name="lastName" label={lastName}>
              <Input />
            </FormItem>
            <FormItem name="mobileNumber" label={phoneNumber}>
              <Input type="tel" />
              <div className="description"> {phoneNumberDescription} </div>
            </FormItem>
            {isGuestUser && (
              <>
                <FormItem name="email" label={emailAddress}>
                  <Input type="email" />
                  <div className="description">{noSpam}</div>
                </FormItem>

                <FormItem name="password" label={createPassword} showErrorsOnSubmit={false}>
                  <PasswordInput minLength={8} minScore={2} checkValidity={checkPasswordValidity} />
                </FormItem>
              </>
            )}
          </div>
          {hasPurchaseOrder && (
            <FormItem name="purchaseOrder" label={purchaseOrder} className="purchase-order">
              <Input />
            </FormItem>
          )}
          {isGuestUser && (
            <>
              <FormItem name="marketingConsent">
                <label className="marketing">
                  <Input type="checkbox" className="marketing-checkbox" id="marketing-checkbox" />
                  <label htmlFor="marketing-checkbox" />
                  <div> {exclusiveDiscountsMessage} </div>
                </label>
              </FormItem>
              <div className="terms">{termsAndConditions}</div>
            </>
          )}
          {inFormCta && (
            <Button designType={DesignType.CTA_DARK} noIcon type="submit" loading={loading}>
              {inFormCta}
            </Button>
          )}
        </Form>
      </Card>
    </Styled>
  );
}
PersonalDetailsForm.displayName = 'PersonalDetailsForm';
export { PersonalDetailsForm };
